/**
 * Phase 40 — DOM word extraction and safe activation layer.
 *
 * Extracts a deduplicated word pool from portfolio sections tagged with
 * data-game-source, then exposes activate/deactivate/restoreAll APIs that
 * mutate the DOM outside React's vDOM (per CONTEXT decision). Phase 41
 * (Word Enemy Core) consumes this module on game activate.
 *
 * Critical findings honored from 40-RESEARCH.md:
 *  - Element-walking, NOT text-node walking, to survive AboutContent's
 *    mid-word <span className="underlined-span"> fragmentation.
 *  - Container visibility check at extraction; per-element visibility
 *    check at activation (VIEW-01, VIEW-02).
 *  - Fallback entries (category: 'fallback') short-circuit activateWord
 *    to avoid mutating document.body.
 *  - Fisher-Yates shuffle (sort/Math.random is biased).
 *  - Dedup order: bio → skills → projects (first occurrence wins).
 */
import type { WordPoolEntry } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const FALLBACK_WORDS: readonly string[] = [
  'react', 'typescript', 'canvas', 'design', 'build',
  'deploy', 'server', 'client', 'render', 'async',
  'layout', 'socket', 'pixel', 'query', 'redux',
  'router', 'module', 'import', 'export', 'fetch',
];

const STOPWORDS: ReadonlySet<string> = new Set([
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can',
  'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him',
  'his', 'how', 'its', 'let', 'may', 'new', 'now', 'old', 'see',
  'two', 'who', 'boy', 'did', 'put', 'say', 'she', 'too', 'use', 'way',
]);

/** DOM-05: supplement when extracted pool has fewer than this many entries. */
const FALLBACK_THRESHOLD = 15;

/** Deduplication order: bio first, skills second, projects third (first occurrence wins). */
const SECTION_ORDER: ReadonlyArray<'bio' | 'skills' | 'projects'> = ['bio', 'skills', 'projects'];

// ---------------------------------------------------------------------------
// Module-level pool ref (CONTEXT decision: extracted once on game activate)
// ---------------------------------------------------------------------------

let _pool: WordPoolEntry[] = [];

export function getWordPool(): readonly WordPoolEntry[] {
  return _pool;
}

// ---------------------------------------------------------------------------
// Extraction helpers
// ---------------------------------------------------------------------------

/** Fisher-Yates in-place shuffle — `sort(() => Math.random() - 0.5)` is biased. */
function shuffleInPlace<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * VIEW-01 container-level viewport gate (coarse check at extraction time).
 * Per-element check happens inside activateWord() at spawn time.
 */
function isContainerInViewport(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

/**
 * Collect words from a data-game-source container into `out`, tracking
 * deduplication state via `seen`.
 *
 * Uses SHOW_ELEMENT TreeWalker to read full `.textContent` at the prose-
 * container level. This survives AboutContent's mid-word
 * <span className="underlined-span"> fragmentation: the <p> element's
 * .textContent concatenates all descendant text including the wrapped
 * letters, so splitting on whitespace yields intact words.
 *
 * Prose containers (P, LI, Hx, etc.) are accepted directly. Standalone
 * leaf SPAN elements outside any prose container (e.g., .tech-grid-name)
 * are also accepted.
 */
function collectWordsFromContainer(
  container: Element,
  category: 'bio' | 'skills' | 'projects',
  seen: Set<string>,
  out: WordPoolEntry[]
): void {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (_node: Node): number => {
        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const PROSE_TAGS = new Set(['P', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'FIGCAPTION', 'BLOCKQUOTE']);
  const candidates: Element[] = [];

  let node: Node | null;
  while ((node = walker.nextNode())) {
    const el = node as Element;

    // Prose containers: read full textContent (absorbs all inline spans).
    if (PROSE_TAGS.has(el.tagName)) {
      candidates.push(el);
      continue;
    }

    // Standalone leaf SPANs outside any prose container (e.g. .tech-grid-name).
    if (el.children.length === 0 && el.tagName === 'SPAN') {
      let parent: Element | null = el.parentElement;
      let inProse = false;
      while (parent && parent !== container) {
        if (PROSE_TAGS.has(parent.tagName)) {
          inProse = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (!inProse) {
        candidates.push(el);
      }
    }
  }

  for (const el of candidates) {
    const text = (el.textContent ?? '').trim();
    if (!text) continue;
    const fontSize = parseFloat(getComputedStyle(el).fontSize) || 14;
    for (const token of text.split(/\s+/)) {
      const word = token.replace(/[^a-zA-Z]/g, '').toLowerCase();
      if (word.length < 3 || word.length > 10) continue;
      if (STOPWORDS.has(word)) continue;
      if (seen.has(word)) continue;
      seen.add(word);
      out.push({ word, sourceEl: el, category, fontSize });
    }
  }
}

// ---------------------------------------------------------------------------
// Public extraction API
// ---------------------------------------------------------------------------

/**
 * Extract a word pool from the live DOM by walking all data-game-source
 * containers currently visible in the viewport.
 *
 * Pure read operation — DOM-03: no spans injected, no nodes replaced.
 * Deduplication order: bio → skills → projects (first occurrence wins).
 * Supplements with FALLBACK_WORDS when fewer than 15 unique words found (DOM-05).
 * Stores result in module-level `_pool`; returns the same array.
 */
export function extractWordPool(): WordPoolEntry[] {
  restoreAll(); // ensure no stale active entries from a prior session
  const seen = new Set<string>();
  const extracted: WordPoolEntry[] = [];

  for (const category of SECTION_ORDER) {
    const container = document.querySelector(`[data-game-source="${category}"]`);
    if (!container) continue;
    // VIEW-01: container-level viewport gate.
    if (!isContainerInViewport(container)) continue;
    collectWordsFromContainer(container, category, seen, extracted);
  }

  // DOM-05: supplement with fallback when fewer than 15 unique words.
  let combined: WordPoolEntry[];
  if (extracted.length < FALLBACK_THRESHOLD) {
    const fallbackEntries: WordPoolEntry[] = FALLBACK_WORDS
      .filter((w) => !seen.has(w))
      .map((word) => ({
        word,
        sourceEl: document.body, // sentinel — activateWord short-circuits on category 'fallback'
        category: 'fallback' as const,
        fontSize: 14,
      }));
    combined = [...extracted, ...fallbackEntries];
  } else {
    combined = extracted;
  }

  shuffleInPlace(combined);
  _pool = combined;
  return _pool;
}

// ---------------------------------------------------------------------------
// Activation state
// ---------------------------------------------------------------------------

interface ActiveEntry {
  span: HTMLSpanElement;
  originalTextNode: Text;
  beforeNode: Text;
  afterNode: Text;
}

const activeWords = new Map<string, ActiveEntry>();

// ---------------------------------------------------------------------------
// Activation helpers
// ---------------------------------------------------------------------------

/**
 * Walk SHOW_TEXT inside `el` to find the first Text node whose textContent
 * contains `word` as a whole word (case-insensitive). Used by activateWord
 * to locate the exact text node to wrap.
 *
 * Returns null if no match found (e.g., the text was already wrapped or
 * removed by a prior activation).
 */
function findTextNodeContaining(el: Element, word: string): Text | null {
  const lowerWord = word.toLowerCase();
  const pattern = new RegExp(`\\b${lowerWord}\\b`, 'i');
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = (node as Text).textContent ?? '';
    if (pattern.test(text)) {
      return node as Text;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Public activation API
// ---------------------------------------------------------------------------

/**
 * Activate a word as a game enemy.
 *
 * Returns the viewport-relative DOMRect of the source element captured
 * AT ACTIVATION TIME (per VIEW-02 — the user may have scrolled since
 * extraction). Phase 41 uses this rect for the enemy's spawn coordinates.
 *
 * Returns null if any of:
 *   - entry.category === 'fallback' (no real source element to hide)
 *   - entry.sourceEl is no longer connected to the document
 *   - entry.sourceEl is currently off-screen (VIEW-01 per-element check)
 *   - the word is already active (idempotent guard)
 *   - no matching text node found inside sourceEl
 *
 * On success, the source text is wrapped in a <span style="visibility:hidden">
 * via parentNode.replaceChild — React's vDOM is unaware of this mutation.
 * Layout is preserved (DOM-04: visibility:hidden, NOT display:none).
 */
export function activateWord(entry: WordPoolEntry): DOMRect | null {
  // Pitfall 5: fallback short-circuit BEFORE any DOM access.
  if (entry.category === 'fallback') {
    return null;
  }

  // Pitfall 3: idempotent guard.
  if (activeWords.has(entry.word)) {
    return null;
  }

  // Pitfall 4: detached node guard.
  if (!entry.sourceEl.isConnected) {
    return null;
  }

  // VIEW-01 + VIEW-02: per-element visibility check at activation time.
  const rect = entry.sourceEl.getBoundingClientRect();
  if (rect.bottom < 0 || rect.top > window.innerHeight) {
    return null;
  }

  // Locate the actual text node to wrap.
  const textNode = findTextNodeContaining(entry.sourceEl, entry.word);
  if (!textNode || !textNode.parentNode) {
    return null;
  }

  // Split the text node into before/word/after so only the matched word
  // is hidden (DOM-04: visibility:hidden preserves layout geometry).
  const fullText = textNode.textContent ?? '';
  const lowerFull = fullText.toLowerCase();
  const idx = lowerFull.indexOf(entry.word.toLowerCase());
  if (idx === -1) return null;

  const before = document.createTextNode(fullText.slice(0, idx));
  const wordText = document.createTextNode(fullText.slice(idx, idx + entry.word.length));
  const after = document.createTextNode(fullText.slice(idx + entry.word.length));

  const span = document.createElement('span');
  span.setAttribute('data-game-active', entry.word);
  span.style.visibility = 'hidden';
  span.appendChild(wordText);

  const parent = textNode.parentNode;
  parent.insertBefore(before, textNode);
  parent.insertBefore(span, textNode);
  parent.insertBefore(after, textNode);
  parent.removeChild(textNode);

  activeWords.set(entry.word, { span, originalTextNode: textNode, beforeNode: before, afterNode: after });

  return rect;
}

/**
 * Remove the span for a single word and restore its original text node.
 * Called by Phase 41 when an enemy is cleared or defeated.
 * No-op if the word is not currently active.
 */
export function deactivateWord(word: string): void {
  const entry = activeWords.get(word);
  if (!entry) return;
  const { span, originalTextNode, beforeNode, afterNode } = entry;
  if (span.parentNode) {
    const parent = span.parentNode;
    parent.insertBefore(originalTextNode, span);
    parent.removeChild(span);
    if (beforeNode.parentNode) parent.removeChild(beforeNode);
    if (afterNode.parentNode) parent.removeChild(afterNode);
  }
  activeWords.delete(word);
}

/**
 * Remove every active span and restore every original text node.
 * Called from GameCanvas cleanup BEFORE GameContext.deactivate() runs
 * — see Pitfall 2: must complete before any React teardown.
 */
export function restoreAll(): void {
  activeWords.forEach(({ span, originalTextNode, beforeNode, afterNode }) => {
    if (span.parentNode) {
      const parent = span.parentNode;
      parent.insertBefore(originalTextNode, span);
      parent.removeChild(span);
      if (beforeNode.parentNode) parent.removeChild(beforeNode);
      if (afterNode.parentNode) parent.removeChild(afterNode);
    }
  });
  activeWords.clear();
}
